import React, { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { DatePicker } from "@mui/x-date-pickers";
import { useLoadPlanServiceCenters } from "../../../hooks/react-query/useServiceCenters";
import { DaylightDateFormat } from "../../../constants/DaylightDateFormat";

interface SelectSicDialogProps {
  open: boolean;
  onClose: () => void;
}

const formSchema = z.object({
  date: z.custom<dayjs.Dayjs>((val) => val && dayjs.isDayjs(val), {
    message: "Select a date",
  }),
  serviceCenter: z.string().min(1, "Please select an origin service center"),
});

type FormInputs = z.infer<typeof formSchema>;

const SelectSicDialog = ({ open, onClose }: SelectSicDialogProps) => {
  const { control, handleSubmit, watch } = useForm<FormInputs>({
    defaultValues: {
      date: dayjs(),
      serviceCenter: "",
    },
    resolver: zodResolver(formSchema),
  });
  const date = watch("date");
  const { data: serviceCenters = [], isLoading: isLoadingServiceCenter } =
    useLoadPlanServiceCenters(date);
  const navigate = useNavigate();

  const handleClosePrevented = (
    e: MouseEvent<HTMLButtonElement | HTMLElement>
  ) => {
    e.preventDefault();
    onClose();
  };

  const handleConfirm = ({ date, serviceCenter }: FormInputs) => {
    navigate(
      `/linehaul/reports/load-plan/${serviceCenter}?date=${date.format(
        DaylightDateFormat.ISO_DATE
      )}`
    );
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClosePrevented}>
      <DialogTitle variant="h5">
        Load Plan Report
        <Box position="absolute" top={10} right={10}>
          <IconButton
            onClick={handleClosePrevented}
            aria-label="Close Load Plan Report Dialog"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form>
          <Typography
            variant="body1"
            fontWeight={500}
            color={colors.grey[800]}
            marginBottom={1}
          >
            Date
          </Typography>
          <Controller
            name="date"
            control={control}
            render={({ field, fieldState }) => (
              <DatePicker
                format={DaylightDateFormat.DATE_WITH_SHORT_YEAR}
                {...field}
                onChange={(val) => field.onChange(val)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  },
                }}
              />
            )}
          />
          <Typography
            variant="body1"
            fontWeight={500}
            color={colors.grey[800]}
            marginTop={1}
          >
            Choose an origin service center
          </Typography>
          <Controller
            name="serviceCenter"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                sx={{ padding: "15px 0px" }}
                loading={isLoadingServiceCenter}
                options={serviceCenters}
                getOptionLabel={(option) => (option.id ? option.code : "")}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
                value={
                  serviceCenters.find((sc) => sc.code === field.value) ?? null
                }
                onChange={(_, newValue) => {
                  field.onChange(newValue?.code);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="Origin SIC"
                    label="Origin SIC"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClosePrevented}
          color="inherit"
          sx={{
            border: "none",
            color: colors.blue[700],
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleConfirm)}
          color="primary"
          sx={{
            ml: 1,
            textTransform: "none",
          }}
        >
          View Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectSicDialog;
